import {
  ARCHETYPES,
  ARCHETYPE_IDS,
  MAX_APPRAISAL_BONUS,
  MIN_APPRAISAL_BONUS,
} from '@drawgon/shared';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppStoreBadge } from '../components/AppStoreBadge';
import { APP_STORE_URL } from '../lib/links';
import {
  badgeAi,
  btnPrimary,
  card,
  cardLive,
  mediaSlot,
  siteContainer,
  tiltLeft,
  tiltRight,
} from '../lib/ui';

/**
 * トップ（#31。Claude Design の `drawgon 公開サイト`）。何のゲームかを伝える。
 *
 * **文言はいまの仕様に合わせてある**（デザインの文言は企画書の時点のもの）。違いは
 * `docs/specs/public-site.md` の「デザインとの違い」。数字はマスターから組み立て、仕様と離れないようにする。
 *
 * 見出しのように**言葉の間に空白を入れて書いた文は `break-keep`** にして、空白のところだけで折る
 * （付けないと、狭い画面で「キャラ／が」のように言葉の途中で折れる）。
 */

/** 企画書 §5 のコアゲームループ。 */
const STEPS = [
  { title: '描く', body: '紙でもアプリでも。自分のキャラクターを描く' },
  { title: 'AIが鑑定する', body: '絵の特徴から素質・系統・特性と、もとの能力値を読み取る' },
  {
    title: 'ボーナスを振る',
    body: `鑑定で決まるボーナス（${MIN_APPRAISAL_BONUS}〜${MAX_APPRAISAL_BONUS}）を HP / ATK / DEF / SPD に振り分ける`,
  },
  { title: '大会へ送り出す', body: '8人のトーナメントへ。見ていない間も試合は進む' },
  { title: '競技人生が積み上がる', body: '戦績・大会の成績・ランキングが残る' },
];

/** 企画書 §3.1 の差別化軸から、利用者に意味のあるものを選んだ。 */
const RULES = [
  {
    title: 'AIは素質を鑑定するだけ',
    body: '勝敗はAIが決めない。再現可能なルールエンジンが計算する。だから結果に理由があり、次の一手を考えられる。',
  },
  {
    title: '同じ素質でも、強さは変わる',
    body: `鑑定で決まるボーナス（${MIN_APPRAISAL_BONUS}〜${MAX_APPRAISAL_BONUS}）をどの能力に振るかで、同じ絵からでも別の選手になる。`,
  },
  {
    title: '絵の上手さでは決まらない',
    body: '評価するのは個性・装備・大きさ・スピード感といった特徴。誰が描いてもある程度は戦える。',
  },
  {
    title: 'キャラクターは使い捨てにしない',
    body: '戦績も、大会の成績も残り続ける。1体のキャラクターに競技人生が生まれる。',
  },
];

export function Home() {
  useScrollToHash();

  return (
    <>
      <section className={`${siteContainer} pt-[clamp(16px,3vw,32px)]`}>
        <div className="mb-[clamp(20px,3vw,32px)] text-center">
          <h1 className="font-display text-[clamp(32px,5.4vw,60px)] leading-[1.25] text-balance break-keep">
            描いた キャラが、世界大会へ。
          </h1>
          <p className="mx-auto mt-3 max-w-[560px] text-[clamp(15px,1.6vw,18px)] leading-[1.8] text-pretty break-keep text-text-muted">
            指で描いた 1体を AI が鑑定し、選手として 大会で たたかわせる ゲーム。
          </p>
        </div>

        <div className={`${card} ${tiltLeft} mx-auto max-w-[1120px] p-[clamp(8px,1.2vw,14px)]`}>
          <HeroMedia />
        </div>

        <div className="mt-[clamp(28px,4vw,40px)] flex flex-wrap items-center justify-center gap-4">
          <AppStoreBadge href={APP_STORE_URL} />
        </div>
      </section>

      <section id="howto" className={`${siteContainer} scroll-mt-4 pt-[clamp(64px,8vw,104px)]`}>
        <h2 className="mb-7 text-center font-display text-xl text-text-muted">遊びかた</h2>
        <ol className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5 min-[1100px]:grid-cols-5">
          {STEPS.map((step, i) => (
            <li key={step.title} className={`${card} px-[22px] pt-6 pb-7`}>
              <div className="font-display tabular">{String(i + 1).padStart(2, '0')}</div>
              <h3 className="mt-2.5 font-display text-[21px] leading-[1.4]">{step.title}</h3>
              <p className="mt-3 text-sm leading-[1.85] text-pretty text-text-muted">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className={`${siteContainer} pt-[clamp(64px,8vw,104px)]`}>
        <h2 className="mb-8 text-center font-display text-[clamp(30px,4vw,44px)] leading-[1.1]">
          このゲームの決めごと
        </h2>
        <div className="grid gap-5 md:grid-cols-2">
          {RULES.map((rule) => (
            <div key={rule.title} className={`${card} px-[30px] py-7`}>
              <h3 className="font-display text-xl">{rule.title}</h3>
              <p className="mt-3 text-[15px] leading-[1.9] text-pretty text-text-muted">
                {rule.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section id="arche" className={`${siteContainer} scroll-mt-4 pt-[clamp(64px,8vw,104px)]`}>
        <h2 className="text-center font-display text-xl text-text-muted">系統</h2>
        <p className="mx-auto mt-4 mb-8 max-w-[760px] text-center text-[15px] leading-[1.9] text-pretty text-text-muted">
          AIが絵から読み取る「戦い方の傾向」。系統ごとに、能力値の生かし方がちがう。
        </p>
        <ul className="grid grid-cols-2 gap-[18px] min-[500px]:grid-cols-3 min-[990px]:grid-cols-6">
          {ARCHETYPE_IDS.map((id) => (
            <li key={id} className={`${card} px-4 pt-[26px] pb-[30px] text-center`}>
              <div className="font-display text-[40px] leading-none">{ARCHETYPES[id].name}</div>
              <p className="mt-3.5 text-[13.5px] leading-[1.8] text-pretty text-text-muted">
                {ARCHETYPES[id].description}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className={`${siteContainer} pt-[clamp(64px,8vw,104px)]`}>
        <div
          className={`${cardLive} ${tiltRight} flex flex-wrap items-center justify-between gap-5 p-[clamp(22px,3vw,32px)]`}
        >
          <div>
            <h2 className="font-display text-[clamp(24px,3vw,32px)] leading-[1.1] break-keep">
              ワールドカップ ランキング
            </h2>
            <p className="mt-2 text-[15px] break-keep">いま 強い 選手を 見てみよう</p>
          </div>
          <Link to="/rankings" className={btnPrimary}>
            ランキングを見る
          </Link>
        </div>
      </section>
    </>
  );
}

/**
 * ヒーローの映像の場所。**いまは置き場所だけ**で、Remotion で作るモーショングラフィックスに差し替える
 * （まだ無い。`docs/specs/public-site.md` の「ヒーロー」）。デザインは 16:9（1920 × 1080）。
 *
 * 飾りなので読み上げない。動画にしたら、内容を伝える代わりの文を付ける。
 */
function HeroMedia() {
  return (
    <div className={`${mediaSlot} scan-area aspect-video`} aria-hidden="true">
      <span className={`${badgeAi} absolute right-3.5 bottom-3.5`}>AI鑑定済み</span>
    </div>
  );
}

/**
 * ヘッダーの「遊びかた」「系統」（`/#howto` など）で、その節まで送る。
 *
 * SPA は中身を描いてから節ができるので、ブラウザの既定のアンカーの移動が効かない。ほかのページから
 * 来たときも、トップの中で押し直したときも送れるように、URL の `#` と移動のたびに変わる `key` を見る。
 */
function useScrollToHash() {
  const { hash, key } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, key]);
}
